import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useFlexLayout, useTable } from 'react-table';
import classnames from 'classnames';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import FormattedCurrencyContainer from '../../../../components/formatted-currency/container';
import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { emptyArray } from 'ui-components/default-props/empty';
import commonMessages from '../common-messages';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const NAME = 'name';
const QTY = 'qty';
const REVENUE = 'revenue';

const dataSourcesToAlign = [QTY, REVENUE];

const messages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Sales by Location',
    id: '9WCMNE',
    description: 'sales by region table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: '58wLSo',
    description: 'sales by region table name header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: '1vOfBV',
    description: 'sales by region table quantity (abbreviated) header',
  },
  [REVENUE]: {
    defaultMessage: 'Revenue',
    id: 'KWn3Z4',
    description: 'sales by region table revenue header',
  },
});

type RegionData = {
  name: string;
  count: number;
  amount: number;
};

type SalesByRegionTableProps = {
  data?: RegionData[];
};

export const SalesByRegionTable = ({ data = emptyArray as RegionData[] }: SalesByRegionTableProps) => {
  const intl = useIntl();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const emptyRow = !data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null;

  const renderAlignedCell = useCallback(
    (value: React.ReactNode, accessor: string) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
      ) : (
        value
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const renderAlignedHeader = useCallback(
    (accessor: string, title: React.ReactNode) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {title}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
      ) : (
        title
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => renderAlignedHeader(NAME, intl.formatMessage(messages[NAME])),
        accessor: NAME as typeof NAME,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, NAME),
      },
      {
        Header: () => renderAlignedHeader(QTY, intl.formatMessage(messages[QTY])),
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, QTY),
      },
      {
        Header: () => renderAlignedHeader(REVENUE, intl.formatMessage(messages[REVENUE])),
        accessor: REVENUE as typeof REVENUE,
        Cell: ({ value }: { value: React.ReactNode }) => renderAlignedCell(value, REVENUE),
      },
    ],
    [intl, renderAlignedCell, renderAlignedHeader],
  );

  const tableData = useMemo(
    () =>
      data.map((region) => ({
        name: region.name,
        qty: intl.formatNumber(region.count),
        revenue: <FormattedCurrencyContainer value={region.amount} />,
      })),
    [data, intl],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames({
          [style['table-title']]: !isShipStationAnalyticsFeatureEnabled,
          [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled,
        })}
      >
        <Text textType={TextType.H3} value={messages[TITLE]} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};

export default SalesByRegionTable;
